import { Dialog, InputV2, TextArea } from '@cimplico/components';
import { Controller, useForm } from 'react-hook-form';
import EntityTypeSelect from '../../components/entity-type-select/EntityTypeSelect';
import { toastNotification } from '../../components/toast/Toast';
import { useCreateCustomWorksheetTemplate } from '../../queries/use-create-custom-worksheet-template';
import { CustomWorksheetTemplate, CWTBehaviourEnum } from '../../types/custom-worksheet-v2';
import { EntityTypeEnum } from '../../types/entity';
import BehaviourRadioButton from '../calculation-template/BehaviourRadioButton';
import OtherScheduleToggle from '../calculation-template/OtherScheduleToggle';

type CreateFormData = {
    name: string;
    description: string;
    tags: string[];
    entityTypes: EntityTypeEnum[];
    allowAsSchedule: boolean;
    behaviour: CWTBehaviourEnum;
};

type DuplicateFormProps = {
    template: CustomWorksheetTemplate;
    publish?: boolean;
    onClose: () => void;
    dialogTitle: string;
    dialogDescription: string;
    successMessage: string;
    primaryAction: string;
};

function CWTDuplicate({
    template,
    onClose,
    publish = false,
    dialogTitle,
    dialogDescription,
    successMessage,
    primaryAction,
}: DuplicateFormProps): JSX.Element {
    const { mutate, isLoading } = useCreateCustomWorksheetTemplate();

    const form = useForm<CreateFormData>({
        defaultValues: {
            name: `${template.name} - copy`,
            description: template.description ?? undefined,
            tags: template.tags,
            entityTypes: template.entityTypes,
            allowAsSchedule: template.allowAsSchedule,
            behaviour: template.behaviour,
        },
    });

    function onSubmit(data: CreateFormData): void {
        mutate(
            {
                name: data.name.trim(),
                tags: data.tags.map((t) => t.trim()).filter((t) => t),
                description: data.description?.trim() === '' ? null : data.description?.trim(),
                schema: template.schema,
                status: publish ? 'PUBLISHED' : undefined,
                entityTypes: data.entityTypes,
                allowAsSchedule: data.allowAsSchedule,
                behaviour: data.behaviour,
            },
            {
                onSuccess: () => {
                    onClose();
                    toastNotification('success', successMessage);
                },
                onError: (err) => {
                    const formErrors: Record<string, string[]> = err.response?.data;
                    Object.entries(formErrors).forEach((entry) => {
                        const k = entry[0] as keyof CreateFormData;
                        form.setError(k, { message: entry[1][0] });
                    });
                },
            }
        );
    }

    return (
        <Dialog
            title={dialogTitle}
            description={dialogDescription}
            isOpen
            primaryAction={primaryAction}
            secondaryAction="Cancel"
            type="info"
            onClose={onClose}
            onSecondaryActionClick={onClose}
            onPrimaryActionClick={form.handleSubmit(onSubmit)}
            isLoading={isLoading}
        >
            <div>
                <form onSubmit={form.handleSubmit(onSubmit)} className="mr-12 pb-4 flex flex-col gap-4">
                    {/* Name */}
                    <Controller
                        name="name"
                        control={form.control}
                        rules={{ required: 'Name is required' }}
                        render={(render): JSX.Element => (
                            <InputV2
                                type="text"
                                value={render.field.value}
                                onChange={render.field.onChange}
                                title="Name"
                                autoFocus
                                errorMessage={render.fieldState.error?.message}
                            />
                        )}
                    />
                    {/* Tag */}
                    <Controller
                        name="tags"
                        control={form.control}
                        rules={{ required: 'Tag is required' }}
                        render={(render): JSX.Element => {
                            return (
                                <InputV2
                                    type="text"
                                    value={render.field.value.join(',')}
                                    onChange={(v): void => render.field.onChange(v.split(','))}
                                    title="Tag"
                                    errorMessage={render.fieldState.error?.message}
                                />
                            );
                        }}
                    />

                    {/* Account */}
                    <OtherScheduleToggle control={form.control} name="allowAsSchedule" />
                    {/* Account behaviour */}
                    <BehaviourRadioButton control={form.control} name="behaviour" />

                    {/* Entity Types */}
                    <Controller
                        name="entityTypes"
                        control={form.control}
                        render={(render): JSX.Element => {
                            return (
                                <EntityTypeSelect
                                    isMulti
                                    title="Entity Types"
                                    isOptional
                                    onChange={render.field.onChange}
                                    defaultEntityType={render.field.value}
                                />
                            );
                        }}
                    />
                    {/* Description */}
                    <Controller
                        name="description"
                        control={form.control}
                        rules={{ required: false }}
                        render={(render): JSX.Element => (
                            <TextArea defaultValue={render.field.value || ''} onChange={render.field.onChange} title="Description" />
                        )}
                    />
                    {/* This is needed to allow "enter" to trigger submit as the dialog "Save" button exists out of the form */}
                    <button type="submit" hidden />
                </form>
            </div>
        </Dialog>
    );
}

export default CWTDuplicate;
